import React from 'react';
import { DesignLayoutType, EventPageType } from '@graphql/generated-base';

import { LazyFaqList } from '../../../../widgets/FaqList/FaqList.lazy';
import { LazyScheduleList } from '../../../../widgets/ScheduleList/ScheduleList.lazy';
import { LazyTravelList } from '../../../../widgets/TravelList/TravelList.lazy';
import { LazyWeddingPartyList } from '../../../../widgets/WeddingPartyList/WeddingPartyList.lazy';
import type { LayoutBrannanProps } from '../../LayoutBrannan';
import { Box } from '@withjoy/joykit';
import { EventLogisticsBlock } from '../../../../components/EventLogisticsBlock';
import { LazyRegistryList } from '@apps/guest/packages/layout-engine/widgets/RegistryList/RegistryList.lazy';
import { CtaButtons } from '@apps/guest/packages/layout-engine/components/CtaButtons/CtaButtons';
import { LazyApps } from '@apps/guest/packages/layout-engine/widgets/Apps/Apps.lazy';
import { LazyCustom } from '@apps/guest/packages/layout-engine/widgets/Custom/Custom.lazy';
import { findEventPageByPageSlug } from '../../../layout.utils';
import { toCanonicalMigratedSlug } from '@shared/utils/migratedCustomPages';
import { EVENT_PAGE_SLUGS } from '@apps/guest/shared/constants/eventPageSlugs';
import { GuestSiteTypographyOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride';
import { ChunkErrorBoundary } from '@shared/components/ChunkErrorBoundary';
import { button2ToButtonsOverride } from '@apps/guest/components/GuestSiteTypographyOverride/GuestSiteTypographyOverride.constants';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useGuestPageContentRedesign } from '@apps/guest/hooks/useGuestPageContentRedesign';
import { getEffectiveScheduleLayout } from '@shared/components/ScheduleLayoutSelector/ScheduleLayoutSelector.utils';
import { getBrannanCenteredScheduleProps } from '../../../../widgets/ScheduleList/ScheduleList.centered.utils';
import { isTravelPageSlug, WeddingMapPageSection } from '../../../../components/WeddingMapPageSection/WeddingMapPageSection';

export type PreparedPages =
  | EventPageType.app
  | EventPageType.registry
  | EventPageType.travel
  | EventPageType.schedule
  | EventPageType.faq
  | EventPageType.vip
  | EventPageType.welcome
  | EventPageType.map
  | EventPageType.custom;

export const pageWidgets: Readonly<Record<PreparedPages, (props: LayoutBrannanProps) => React.ReactElement>> = {
  [EventPageType.custom]: props => {
    // Migrated registrylinks events keep their existing registry-links listing as the body.
    // Render it without the centering wrapper so the visual output is identical to the
    // legacy `EventPageType.registry` branch (any extra wrapper introduces padding drift on
    // flag rollback).
    const isMigratedRegistryPage = toCanonicalMigratedSlug(props.pageSlug) === EVENT_PAGE_SLUGS.Registry;
    if (isMigratedRegistryPage) {
      return (
        <ChunkErrorBoundary section="guest/widget/registryList">
          <LazyRegistryList
            eventId={props.event.id}
            eventHandle={props.eventHandle}
          />
        </ChunkErrorBoundary>
      );
    }
    return (
      <ChunkErrorBoundary section="guest/widget/custom">
        <Box
          data-testid={props.isWelcomePage ? 'page-custom-welcome' : 'page-custom'}
          display={'flex'}
          flexDirection={'column'}
          justifyContent="center"
          alignItems="center"
          width="100%"
        >
          <LazyCustom
            pageId={props.pageId || ''}
            eventId={props.event.id}
            pageSlug={props.pageSlug || ''}
            pageTitle={props.pageTitle}
            isBrannanLayout
            subTitle={props.subTitle}
            eventHandle={props.eventHandle}
            pages={props.event.pages}
          />
          {/*
            Events built in the current editor have no typed `travel` page —
            theirs is a custom page whose slug is `travel`, so the branch above
            never sees them. Renders nothing outside the rollout.
          */}
          {isTravelPageSlug(props.pageSlug) ? <WeddingMapPageSection eventHandle={props.eventHandle} /> : null}
        </Box>
      </ChunkErrorBoundary>
    );
  },
  [EventPageType.app]: () => (
    <ChunkErrorBoundary section="guest/widget/apps">
      <Box
        data-testid={'page-apps'}
        width="100%"
      >
        <LazyApps data-testid={'page-app'} />
      </Box>
    </ChunkErrorBoundary>
  ),
  [EventPageType.faq]: props => (
    <ChunkErrorBoundary section="guest/widget/faqList">
      <LazyFaqList
        data-testid={'page-faq'}
        eventHandle={props.eventHandle}
      />
    </ChunkErrorBoundary>
  ),
  [EventPageType.registry]: props => (
    <ChunkErrorBoundary section="guest/widget/registryList">
      <LazyRegistryList
        eventId={props.event.id}
        eventHandle={props.eventHandle}
      />
    </ChunkErrorBoundary>
  ),
  [EventPageType.schedule]: props => {
    const schedulePage = findEventPageByPageSlug(props.event.pages, 'schedule');
    const { enabled: isRedesignEnabled } = useGuestPageContentRedesign();
    const effectiveLayoutType = getEffectiveScheduleLayout(schedulePage?.layoutType, isRedesignEnabled);
    return (
      <ChunkErrorBoundary section="guest/widget/scheduleList">
        <LazyScheduleList
          data-testid={'page-schedule'}
          eventHandle={props.eventHandle}
          eventId={props.event.id}
          layoutType={effectiveLayoutType}
          pageId={schedulePage?.id}
          {...getBrannanCenteredScheduleProps(effectiveLayoutType, DesignLayoutType.brannan)}
        />
      </ChunkErrorBoundary>
    );
  },
  [EventPageType.travel]: props => (
    <>
      <ChunkErrorBoundary section="guest/widget/travelList">
        <LazyTravelList
          data-testid={'page-travel'}
          eventHandle={props.eventHandle}
          eventId={props.event.id}
          useMiniNav={false}
          pages={props.event?.pages}
        />
      </ChunkErrorBoundary>
      {/* Renders nothing outside the rollout — see WeddingMapPageSection. */}
      <WeddingMapPageSection eventHandle={props.eventHandle} />
    </>
  ),
  /*
    The Wedding Map as a page of its own, reached from the nav entry the `map`
    EventPage adds. `variant="page"` rather than `section`: this column is the
    map's, so it keeps the side list the Travel section drops — the list is
    where the place names and nightly rates are.

    Renders `null` outside the rollout, like the Travel section. The nav entry
    is filtered separately by `withoutHiddenMapPage`, so a guest outside the
    rollout neither sees the link nor reaches the map by typing the URL.
  */
  [EventPageType.map]: props => (
    <Box
      data-testid="page-map"
      width="100%"
    >
      <WeddingMapPageSection
        eventHandle={props.eventHandle}
        variant="page"
      />
    </Box>
  ),
  [EventPageType.vip]: props => (
    <ChunkErrorBoundary section="guest/widget/weddingPartyList">
      <LazyWeddingPartyList
        data-testid={'page-vip'}
        graphicAccent={props.event.eventDesign?.accent}
        eventId={props.event.id}
        eventHandle={props.eventHandle}
      />
    </ChunkErrorBoundary>
  ),
  [EventPageType.welcome]: props => {
    const { eventInfo } = useEventInfo();
    return (
      <Box data-testid={'page-welcome'}>
        <EventLogisticsBlock
          location={props.event.info.location}
          date={props.event.info.finalizedDate}
          hideEventCountdown={eventInfo?.settings?.hideEventCountdown ?? false}
          pages={props.event.pages}
        >
          <GuestSiteTypographyOverride override={button2ToButtonsOverride}>
            <CtaButtons />
          </GuestSiteTypographyOverride>
        </EventLogisticsBlock>
      </Box>
    );
  },
};
